// Slide 4 — Acting Forces.  TEST-THEN-REVEAL pattern.
//
//   Phase A (lead-in narration + quiz):  empty aircraft + "Recall the four
//     forces" prompt.  No answers visible.
//   Phase B (after the student answers correctly): celebration banner +
//     all four force arrows animate in one after another (LIFT → WEIGHT →
//     THRUST → DRAG), each with its own subtle stagger so it still feels
//     like a build, not an instant dump.
//
// Driven purely by the `quizCorrect` prop from the lesson player.  Audio
// progress is intentionally NOT used here — we don't want to give away the
// answer while the narrator is asking the question.
import React, { useEffect, useState } from "react";

const FORCES = [
  { key: "lift",   color: "#FFA300", delay: 0,    label: "LIFT",   from: [400, 230], to: [400, 60],  labelPos: [400, 36],  anchor: "middle" },
  { key: "weight", color: "#FFA300", delay: 350,  label: "WEIGHT", from: [400, 270], to: [400, 460], labelPos: [400, 484], anchor: "middle" },
  { key: "thrust", color: "#FFA300", delay: 700,  label: "THRUST", from: [370, 250], to: [160, 250], labelPos: [120, 250], anchor: "end" },
  { key: "drag",   color: "#FFA300", delay: 1050, label: "DRAG",   from: [430, 250], to: [640, 250], labelPos: [680, 250], anchor: "start" },
];

export default function Slide04ActingForces({ quizCorrect, slideQuizFlags = [] }) {
  // Reveal once the (only) quiz on this slide has been answered correctly.
  // `slideQuizFlags[0]` keeps the answer revealed even if the user navigates
  // back or forward within the same slide.
  const revealAnswered = quizCorrect || slideQuizFlags[0];
  // Track an internal "play head" so the four forces stagger in after the
  // student answers correctly.  We bump a single counter once revealAnswered
  // flips true; CSS handles the rest of the timing via `transitionDelay`.
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (revealAnswered) {
      const t = setTimeout(() => setRevealed(true), 50);
      return () => clearTimeout(t);
    }
    setRevealed(false);
  }, [revealAnswered]);

  return (
    <div className="w-full h-full relative flex items-center justify-center overflow-hidden">
      {/* Header strip */}
      <div className="absolute top-6 left-8 right-8 flex items-baseline justify-between">
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-white/50 font-semibold">
          Revision
        </div>
        <div className="text-2xl sm:text-3xl font-heading font-bold text-white tracking-tight">
          Acting Forces
        </div>
      </div>

      {/* Question prompt during the test phase */}
      {!revealAnswered && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-sm border border-[#FFA300]/40 bg-[#FFA300]/10 backdrop-blur-sm">
          <span className="text-[#FFA300] text-sm uppercase tracking-[0.16em] font-bold">
            Recall the four forces acting on an aircraft
          </span>
        </div>
      )}

      {/* Celebration banner appears after the correct answer */}
      {revealAnswered && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-sm border border-[#7BD389]/50 bg-[#7BD389]/15 backdrop-blur-sm fade-up">
          <span className="text-[#7BD389] text-sm uppercase tracking-[0.16em] font-bold">
            ✓ Correct — the four forces are…
          </span>
        </div>
      )}

      <svg viewBox="0 0 800 500" className="w-[92%] h-[78%] mt-12" style={{ overflow: "visible" }}>
        {/* Aircraft icon — always visible, in the centre */}
        <g transform="translate(400 250)">
          <rect x="-8" y="-90" width="16" height="180" rx="8" fill="#cdd9e8" />
          <rect x="-140" y="-12" width="280" height="22" rx="6" fill="#cdd9e8" />
          <rect x="-46" y="62" width="92" height="14" rx="4" fill="#cdd9e8" />
          <ellipse cx="0" cy="-50" rx="10" ry="22" fill="#9ab1cc" />
          <circle cx="0" cy="-92" r="6" fill="#7d97b5" />
        </g>

        {/* Forces — staggered reveal */}
        {FORCES.map((f) => (
          <ForceArrow key={f.key} {...f} show={revealed} />
        ))}
      </svg>

      <div className="absolute bottom-6 left-8 right-8 text-center text-white/70 text-xs sm:text-sm tracking-wide">
        {revealAnswered
          ? "LIFT · WEIGHT · THRUST · DRAG"
          : "(diagram will reveal after your answer)"}
      </div>
    </div>
  );
}

function ForceArrow({ show, from, to, label, labelPos, color, anchor, delay }) {
  const [x1, y1] = from;
  const [x2, y2] = to;
  return (
    <g
      style={{
        opacity: show ? 1 : 0,
        transform: show ? "scale(1)" : "scale(0.55)",
        transformOrigin: `${x1}px ${y1}px`,
        transition: `opacity 500ms cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 500ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
      }}
    >
      <defs>
        <marker
          id={`arr-${label}`}
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
        </marker>
      </defs>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth="5"
        strokeLinecap="round"
        markerEnd={`url(#arr-${label})`}
      />
      <text
        x={labelPos[0]}
        y={labelPos[1]}
        fill="#fff"
        fontSize="22"
        fontWeight="700"
        textAnchor={anchor}
        dominantBaseline="middle"
        fontFamily="Chivo, sans-serif"
        letterSpacing="0.05em"
      >
        {label}
      </text>
    </g>
  );
}
